import React from 'react';
import { Box, Divider, Paper, Stack } from '@mui/material';
import { AlertSwitch, Select, Switch } from '@travelity/form';
import { useFormContext } from 'react-hook-form';
import { UserSubscription, TransportationMedium } from '@travelity/api';
import { useUserContext } from '../../contexts/user';
import { Onboarding } from '../../onboarding/onboarding.component';
import {
    OnboardingClasses,
    OnboardingTours,
    useOnboardingTour,
} from '../../onboarding';

export interface ProductConfigurationFormProps {
    hideDisabled?: boolean;
    preview?: boolean;
    isUpdate?: boolean;
}

const transportationOptions = [
    {
        value: TransportationMedium.LAND,
        label: 'Land',
    },
    {
        value: TransportationMedium.AIR,
        label: 'Air',
    },
    {
        value: TransportationMedium.WATER,
        label: 'Water',
    },
];

const ProductConfigurationForm: React.FC<ProductConfigurationFormProps> = ({
    hideDisabled,
    preview,
    isUpdate = true,
}) => {
    const { user } = useUserContext();
    const { onboardingProps } = useOnboardingTour(
        OnboardingTours.PRODUCT_CONFIGURATION
    );
    const disableAutoscaled = (user?.subscription as UserSubscription).user
        ?.product
        ? !(user?.subscription as UserSubscription).user?.product?.autoscaled
        : false;
    const { watch } = useFormContext();
    const hide = hideDisabled && preview;

    return (
        <Stack spacing={2}>
            <Select
                disabled={preview || isUpdate}
                name="transportationMedium"
                label="Transportation by"
                placeholder="Select Transportation by"
                options={transportationOptions}
            />
            {(!hide || watch('shared')) && (
                <Paper
                    className={OnboardingClasses.GROUP_TOUR_TOGGLE}
                    sx={{
                        py: 1,
                        px: 2,
                        maxWidth: { xs: '100%', sm: '388px' },
                        ...(preview
                            ? {
                                  opacity: 0.6,
                                  pointerEvents: 'none',
                              }
                            : {}),
                    }}
                >
                    <Switch
                        name="shared"
                        label="Group Tour"
                        LabelProps={{ sx: { color: '#2B395B' } }}
                    />
                </Paper>
            )}
            {(!hide || watch('autoscaled')) && (
                <Paper
                    className={OnboardingClasses.AUTOSCALE_TOGGLE}
                    sx={{
                        '&&': { mt: 1.5 },
                        py: 1,
                        px: 2,
                        maxWidth: { xs: '100%', sm: '388px' },
                        ...(preview
                            ? {
                                  opacity: 0.6,
                                  pointerEvents: 'none',
                              }
                            : {}),
                    }}
                >
                    <Switch
                        name="autoscaled"
                        label="Autoscaled"
                        LabelProps={{ sx: { color: '#2B395B' } }}
                        disabled={disableAutoscaled}
                    />
                </Paper>
            )}
            {/* TODO: add onboarding for this */}
            <Paper
                // className={OnboardingClasses.GROUP_TOUR_TOGGLE}
                sx={{
                    py: 1,
                    px: 2,
                    maxWidth: { xs: '100%', sm: '388px' },
                    ...(preview
                        ? {
                              opacity: 0.6,
                              pointerEvents: 'none',
                          }
                        : {}),
                }}
            >
                <Switch
                    name="sell_via_widget"
                    label="Sell via Widget"
                    LabelProps={{ sx: { color: '#2B395B' } }}
                />
            </Paper>

            <Divider
                sx={{
                    maxWidth: '388px',
                }}
            />
            <Box
                sx={{
                    py: 1,
                    maxWidth: { xs: '100%', sm: '388px' },
                    ...(preview || isUpdate
                        ? {
                              opacity: 0.6,
                              pointerEvents: 'none',
                          }
                        : {}),
                }}
            >
                <AlertSwitch
                    name="singleUse"
                    label="Will this product be booked once?"
                    severity="info"
                    disabled={!!preview || isUpdate}
                />
            </Box>
            {!preview && !isUpdate && <Onboarding {...onboardingProps} />}
        </Stack>
    );
};

export default React.memo(ProductConfigurationForm);
